import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import BugReportRecorder from './BugReportRecorder';
import './HelpWidget.css';

// One collapsible corner hub for the two things a user might reach for from
// anywhere: reporting a bug (any user, by voice) and — for admins — the
// announcement replies panel. Each entry can be hidden individually and the
// choice is remembered; hiding both drops the hub to a small restore dot.
const LS = {
    get bugHidden() { try { return localStorage.getItem('helpwidget.bugHidden') === '1'; } catch { return false; } },
    set bugHidden(v) { try { localStorage.setItem('helpwidget.bugHidden', v ? '1' : '0'); } catch { /* ignore */ } },
    get repliesHidden() { try { return localStorage.getItem('helpwidget.repliesHidden') === '1'; } catch { return false; } },
    set repliesHidden(v) { try { localStorage.setItem('helpwidget.repliesHidden', v ? '1' : '0'); } catch { /* ignore */ } }
};

const HIDDEN_ON = ['/login', '/feedback', '/visitor', '/unauthorized'];

const HelpWidget = () => {
    const { user } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [showRecorder, setShowRecorder] = useState(false);
    const [bugHidden, setBugHidden] = useState(LS.bugHidden);
    const [repliesHidden, setRepliesHidden] = useState(LS.repliesHidden);
    const rootRef = useRef(null);

    const isAdmin = user?.role === 'system_admin';

    useEffect(() => {
        if (!open) return undefined;
        const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    if (!user) return null;
    if (HIDDEN_ON.some((p) => location.pathname.startsWith(p))) return null;

    const showBug = !bugHidden;
    const showReplies = isAdmin && !repliesHidden;

    const setBug = (v) => { LS.bugHidden = v; setBugHidden(v); };
    const setReplies = (v) => { LS.repliesHidden = v; setRepliesHidden(v); };

    // Everything hidden → a tiny restore dot, nothing more.
    if (!showBug && !showReplies) {
        return (
            <button
                type="button"
                className="help-widget__dot"
                title="Show help buttons"
                onClick={() => { setBug(false); setReplies(false); }}
            />
        );
    }

    const openReplies = () => {
        setOpen(false);
        window.dispatchEvent(new CustomEvent('announcement:open-dock'));
    };

    return (
        <div className="help-widget" ref={rootRef}>
            {open && (
                <div className="help-widget__menu" role="menu">
                    {showBug && (
                        <button type="button" className="help-widget__item" onClick={() => { setOpen(false); setShowRecorder(true); }}>
                            <span>🎤</span> Report a bug
                        </button>
                    )}
                    {showReplies && (
                        <button type="button" className="help-widget__item" onClick={openReplies}>
                            <span>📢</span> Announcement replies
                        </button>
                    )}
                    <div className="help-widget__sep" />
                    {showBug && (
                        <button type="button" className="help-widget__toggle" onClick={() => setBug(true)}>
                            Hide “Report a bug”
                        </button>
                    )}
                    {showReplies && (
                        <button type="button" className="help-widget__toggle" onClick={() => setReplies(true)}>
                            Hide “Announcement replies”
                        </button>
                    )}
                    {(bugHidden || repliesHidden) && (
                        <button type="button" className="help-widget__toggle" onClick={() => { setBug(false); setReplies(false); }}>
                            Show all
                        </button>
                    )}
                </div>
            )}

            <button
                type="button"
                className={`help-widget__fab ${open ? 'is-open' : ''}`}
                onClick={() => setOpen((o) => !o)}
                title="Help & bug report"
                aria-label="Help and bug report"
            >
                {open ? '✕' : '🐞'}
            </button>

            {showRecorder && <BugReportRecorder onClose={() => setShowRecorder(false)} />}
        </div>
    );
};

export default HelpWidget;
